import React, { useRef } from 'react';
import { FaCertificate, FaDownload, FaShare } from 'react-icons/fa';

const Certificate = ({ student, course, issueDate }) => {
    const certificateRef = useRef();
    const certificateId = `CERT-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;

    const handleDownload = () => {
        // Create a canvas element to draw the certificate
        const certificate = certificateRef.current;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas size (A4 proportions)
        canvas.width = 1200;
        canvas.height = 850;
        
        // Background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Border
        ctx.strokeStyle = '#0d6efd';
        ctx.lineWidth = 10;
        ctx.strokeRect(20, 20, canvas.width - 40, canvas.height - 40);
        
        // Inner border
        ctx.strokeStyle = '#0d6efd';
        ctx.lineWidth = 2;
        ctx.strokeRect(40, 40, canvas.width - 80, canvas.height - 80);
        
        // Header - Certificate Icon (using text as icon replacement)
        ctx.fillStyle = '#0d6efd';
        ctx.font = 'bold 80px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('🎓', canvas.width / 2, 140);
        
        // Title
        ctx.fillStyle = '#0d6efd';
        ctx.font = 'bold 48px Arial';
        ctx.fillText('Certificate of Completion', canvas.width / 2, 220);
        
        // Subtitle
        ctx.fillStyle = '#333333';
        ctx.font = '24px Arial';
        ctx.fillText('This is to certify that', canvas.width / 2, 290);
        
        // Student Name
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 40px Arial';
        ctx.fillText(student.name, canvas.width / 2, 360);
        
        // Completion text
        ctx.fillStyle = '#333333';
        ctx.font = '24px Arial';
        ctx.fillText('has successfully completed the course', canvas.width / 2, 420);
        
        // Course Title
        ctx.fillStyle = '#0d6efd';
        ctx.font = 'bold 32px Arial';
        const maxWidth = canvas.width - 200;
        const courseTitle = course.title;
        if (ctx.measureText(courseTitle).width > maxWidth) {
            ctx.font = 'bold 28px Arial';
        }
        ctx.fillText(courseTitle, canvas.width / 2, 480);
        
        // Issue Date
        ctx.fillStyle = '#666666';
        ctx.font = '20px Arial';
        ctx.fillText(`Issued on ${issueDate}`, canvas.width / 2, 540);
        
        // Instructor section
        ctx.fillStyle = '#333333';
        ctx.font = '18px Arial';
        ctx.textAlign = 'left';
        ctx.fillText('Course Instructor', 150, 680);
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 20px Arial';
        const instructorName = typeof course.instructor === 'object' ? course.instructor.name : course.instructor;
        ctx.fillText(instructorName, 150, 710);
        
        // Line under instructor name
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(150, 720);
        ctx.lineTo(450, 720);
        ctx.stroke();
        
        // Platform section
        ctx.fillStyle = '#333333';
        ctx.font = '18px Arial';
        ctx.textAlign = 'right';
        ctx.fillText('Fundisha Platform', canvas.width - 150, 680);
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 20px Arial';
        ctx.fillText(`ID: ${certificateId}`, canvas.width - 150, 710);
        
        // Line under platform
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(canvas.width - 450, 720);
        ctx.lineTo(canvas.width - 150, 720);
        ctx.stroke();
        
        // Footer
        ctx.fillStyle = '#999999';
        ctx.font = '16px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Verify this certificate at fundisha.com/verify/' + certificateId, canvas.width / 2, 800);
        
        // Download the certificate
        canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${student.name.replace(/\s+/g, '_')}_${course.title.replace(/\s+/g, '_')}_Certificate.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        });
    };

    const handleShare = () => {
        const shareText = `🎓 I just completed "${course.title}" on Fundisha! Certificate ID: ${certificateId}`;
        const shareUrl = window.location.href;
        
        if (navigator.share) {
            navigator.share({
                title: 'My Fundisha Certificate',
                text: shareText,
                url: shareUrl
            }).catch((error) => console.log('Error sharing:', error));
        } else {
            // Fallback - copy to clipboard
            navigator.clipboard.writeText(`${shareText}\n${shareUrl}`)
                .then(() => alert('Certificate details copied to clipboard! You can now paste and share it.'))
                .catch(() => alert('Please manually copy this:\n' + shareText));
        }
    };

    return (
        <div className="card certificate-card" ref={certificateRef}>
            <div className="card-body text-center p-5">
                <div className="certificate-header mb-4">
                    <FaCertificate className="fa-4x text-primary mb-3" />
                    <h2 className="text-primary">Certificate of Completion</h2>
                </div>

                <div className="certificate-content mb-4">
                    <p className="lead">This is to certify that</p>
                    <h3 className="student-name mb-3">{student.name}</h3>
                    <p className="lead">has successfully completed the course</p>
                    <h4 className="course-name mb-3">{course.title}</h4>
                    <p className="text-muted">Issued on {issueDate}</p>
                </div>

                <div className="certificate-footer">
                    <div className="row">
                        <div className="col-md-6">
                            <div className="instructor-signature">
                                <p className="mb-1">Course Instructor</p>
                                <p className="fw-bold">{typeof course.instructor === 'object' ? course.instructor.name : course.instructor}</p>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="platform-signature">
                                <p className="mb-1">Fundisha Platform</p>
                                <p className="fw-bold">Certificate ID: {certificateId}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="certificate-actions mt-4">
                    <button 
                        className="btn btn-primary me-2"
                        onClick={handleDownload}
                    >
                        <FaDownload className="me-2" />
                        Download Certificate
                    </button>
                    <button 
                        className="btn btn-outline-primary"
                        onClick={handleShare}
                    >
                        <FaShare className="me-2" />
                        Share Certificate
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Certificate; 